import { isGhmSignalKind } from "@ghm/mqtt-contract";

import { parseTag } from "@/features/machines/addressing";
import type { SignalType } from "@/features/machines/types";
import type { WidgetSize, WidgetThreshold, WidgetTone, WidgetType } from "@/features/widgets/types";
import { WIDGET_REGISTRY } from "@/features/widgets/registry";
import type { SignalConfig, SignalRole, SignalScale, SignalStateLabels } from "./types";

const SIGNAL_ROLES = new Set<SignalRole>([
  "state",
  "total",
  "speed",
  "flow",
  "direction",
  "technical",
]);
const WIDGET_TYPES = new Set<WidgetType>(WIDGET_REGISTRY.map((definition) => definition.type));
const WIDGET_SIZES = new Set<WidgetSize>(["sm", "md", "lg"]);
const THRESHOLD_TONES = new Set<Exclude<WidgetTone, "neutral">>(["ok", "warn", "danger", "info"]);

export function getSignalConfigStorageKey(
  workspaceId: string,
  machineId: string,
  configVersion?: string | null,
  userId?: string | null,
): string {
  // Versionado por configHash, igual aos widgets (P0.5). Sem isto, um
  // override de label/unidade/limite do operador (ex.: "Velocidade m/min")
  // sobrevivia a um reflash e grudava num sinal que mudou de tipo (ex.: a
  // porta virou contador), mentindo na tela. Hardware é a fonte da verdade:
  // mudou a config, o painel se recompõe a partir do novo meta.
  const userScope = userId?.trim();
  const base = userScope
    ? `ghm:signal-configs:${userScope}:${workspaceId}:${machineId}`
    : getLegacySignalConfigStorageKey(workspaceId, machineId);
  const version = configVersion?.trim();
  return version ? `${base}:${version}` : base;
}

export function getLegacySignalConfigStorageKey(
  workspaceId: string,
  machineId: string,
  configVersion?: string | null,
): string {
  const base = `ghm:signal-configs:${workspaceId}:${machineId}`;
  const version = configVersion?.trim();
  return version ? `${base}:${version}` : base;
}

export function normalizeSignalConfigs(value: unknown, machineId: string): SignalConfig[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((candidate): SignalConfig[] => {
    if (!candidate || typeof candidate !== "object") return [];
    const item = candidate as Record<string, unknown>;
    const source = typeof item.source === "string" ? item.source : "";
    const address = source.startsWith("G") ? parseTag(source) : null;
    if (!address || !address.signal) return [];

    const preferredWidget = item.preferredWidget;
    if (typeof preferredWidget !== "string" || !WIDGET_TYPES.has(preferredWidget as WidgetType)) {
      return [];
    }

    // Cura o "leak de visão derivada": um sinal FÍSICO (`s`/`acc`/`v`) nunca
    // tem "%" como unidade — é sempre metragem, vazão, contagem ou estado. A
    // porcentagem (e a escala 0–100) pertencem à disponibilidade/utilização,
    // que é um TIPO DE WIDGET, não o sinal. Um save antigo desses widgets
    // gravava "%" na config COMPARTILHADA da porta e todos os outros cards
    // herdavam a unidade errada (ex.: "Distancia acumulada" exibindo
    // "52.004 %" em vez de metros). Ao carregar, descartamos esses campos.
    const healed = healDerivedViewUnitLeak(
      typeof item.unit === "string" && item.unit.trim() ? item.unit.trim() : undefined,
      normalizeScale(item.scale),
    );

    return [
      {
        source,
        machineId,
        label: normalizeLabel(item.label, source),
        signal: address.signal as SignalType,
        role: normalizeRole(item.role),
        // Config gravada antes deste campo nao tem como provar escolha: cai em
        // "automatico", e o nome do `/meta` volta a valer. E o que faz o
        // conserto alcancar dashboards que ja existem.
        labelFromOperator: item.labelFromOperator === true ? true : undefined,
        kind: isGhmSignalKind(item.kind) ? item.kind : undefined,
        unit: healed.unit,
        activeWhen: item.activeWhen === 0 || item.activeWhen === 1 ? item.activeWhen : undefined,
        stateLabels: normalizeStateLabels(item.stateLabels),
        scale: healed.scale,
        preferredWidget: preferredWidget as WidgetType,
        hidden: item.hidden === true ? true : undefined,
        size: normalizeSize(item.size),
        thresholds: normalizeThresholds(item.thresholds),
      },
    ];
  });
}

function normalizeSize(value: unknown): WidgetSize | undefined {
  // Migração suave: o enum antigo aceitava `wide`. Converte para `lg`
  // (que agora ocupa 2 colunas + valor grande no novo CSS).
  if (value === "wide") return "lg";
  return typeof value === "string" && WIDGET_SIZES.has(value as WidgetSize)
    ? (value as WidgetSize)
    : undefined;
}

function normalizeThresholds(value: unknown): WidgetThreshold[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: WidgetThreshold[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const candidate = item as Record<string, unknown>;
    const at = finiteNumberOrUndefined(candidate.at);
    const tone = candidate.tone;
    if (at === undefined) continue;
    if (typeof tone !== "string" || !THRESHOLD_TONES.has(tone as Exclude<WidgetTone, "neutral">)) continue;
    const direction = candidate.direction === "below" ? "below" : undefined;
    out.push({
      at,
      tone: tone as Exclude<WidgetTone, "neutral">,
      ...(direction ? { direction } : null),
      label: typeof candidate.label === "string" ? candidate.label : undefined,
    });
  }
  return out.length > 0 ? out.sort((a, b) => a.at - b.at) : undefined;
}

export function loadLocalSignalConfigs(
  storage: Pick<Storage, "getItem">,
  key: string,
  machineId: string,
): SignalConfig[] {
  const raw = storage.getItem(key);
  if (!raw) return [];

  try {
    return normalizeSignalConfigs(JSON.parse(raw), machineId);
  } catch {
    return [];
  }
}

export function saveLocalSignalConfigs(
  storage: Pick<Storage, "setItem">,
  key: string,
  configs: SignalConfig[],
) {
  storage.setItem(key, JSON.stringify(configs));
}

export function indexSignalConfigs(configs: SignalConfig[]): Map<string, SignalConfig> {
  return new Map(configs.map((config) => [config.source, config]));
}

function normalizeLabel(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function normalizeRole(value: unknown): SignalRole {
  return typeof value === "string" && SIGNAL_ROLES.has(value as SignalRole)
    ? (value as SignalRole)
    : "technical";
}

function normalizeStateLabels(value: unknown): SignalStateLabels | undefined {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  if (typeof item.on !== "string" || typeof item.off !== "string") return undefined;

  return {
    on: item.on.trim() || "Ligado",
    off: item.off.trim() || "Desligado",
    unavailable:
      typeof item.unavailable === "string" && item.unavailable.trim()
        ? item.unavailable.trim()
        : "Sem sinal",
  };
}

/**
 * Detecta e remove a unidade "%" (e a escala 0–100 que a acompanha) deixada
 * por um widget de disponibilidade/utilização na config de sinal físico. A
 * escala só é descartada quando bate exatamente a assinatura da visão (0–100,
 * 0 casas) para não apagar uma escala física legítima que por acaso vá a 100.
 */
function healDerivedViewUnitLeak(
  unit: string | undefined,
  scale: SignalScale | undefined,
): { unit: string | undefined; scale: SignalScale | undefined } {
  if (unit !== "%") return { unit, scale };
  const isAvailabilityScale =
    scale !== undefined && scale.min === 0 && scale.max === 100 && scale.decimals === 0;
  return { unit: undefined, scale: isAvailabilityScale ? undefined : scale };
}

function normalizeScale(value: unknown): SignalScale | undefined {
  if (!value || typeof value !== "object") return undefined;
  const item = value as Record<string, unknown>;
  const min = finiteNumberOrUndefined(item.min);
  const max = finiteNumberOrUndefined(item.max);
  const decimals = integerOrUndefined(item.decimals);
  if (min === undefined || max === undefined || decimals === undefined || max <= min) return undefined;
  return { min, max, decimals };
}

function finiteNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function integerOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isInteger(value) ? value : undefined;
}
